import * as React from "react";
import { forwardRef, useImperativeHandle, useRef, useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { QuickAddProductModal } from "./QuickAddProductModal";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { DecimalInput } from "@/components/ui/decimal-input";
import { Button } from "@/components/ui/button";
import { Trash2, ChevronUp, ChevronDown, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { useUnitConversion } from "@/hooks/useUnitConversion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useFormWithFields } from "@/hooks/useForms";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateField } from "@/components/ui/date-field";
import { startOfDay, endOfDay, subDays } from "date-fns";
import ProductDetailsView from "@/pages/product/ProductDetailsView";

type SaleItem = {
  product_id: string;
  quantidade: number;
  preco_unitario: number;
  desconto: number;
  total: number;
  form_values?: Record<string, any>;
};

type Product = {
  id: string;
  nome: string;
  codigo_sku: string | null;
  descricao: string | null;
  preco_venda: number;
  status: string;
  form_id: string | null;
  unit_measurement_purchase_id: string | null;
  unit_measurement_sale_id: string | null;
  unit_measurement_conversions: Record<string, Record<string, number>> | null;
  quantity_decimal_places: number;
  foto_url: string | null;
  created_at: string | null;
  product_category_id: string | null;
};

type UnitMeasurement = {
  id: string;
  nome: string;
  slug: string;
};

interface SaleItemsTableProps {
  items: SaleItem[];
  onItemsChange: (items: SaleItem[]) => void;
  onProductSelectOpenChange?: (open: boolean) => void;
  readOnly?: boolean;
}

export type SaleItemsTableHandle = {
  openNewRowProductSelect: () => void;
  focusQuantity: (index?: number) => void;
  closeProductSelect: () => void;
};

const TextFieldComponent = React.memo(({ field, fieldValue, updateFormValue, readOnly, prefix = "" }: { field: any; fieldValue: string; updateFormValue: (slug: string, value: any) => void; readOnly: boolean; prefix?: string }) => {
  const [localValue, setLocalValue] = React.useState(fieldValue);
  const updateFormValueRef = React.useRef(updateFormValue);
  React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
  React.useEffect(() => { setLocalValue(fieldValue); }, [fieldValue]);
  return <div className="space-y-2"><Label htmlFor={`form-${prefix}${field.id}`}>{field.nome}{field.obrigatorio && <span className="text-destructive"> *</span>}</Label><Input id={`form-${prefix}${field.id}`} value={localValue} onChange={(e) => setLocalValue(e.target.value)} onBlur={() => updateFormValueRef.current(field.slug, localValue)} placeholder={field.placeholder || ""} disabled={readOnly} />{field.descricao && <p className="text-xs text-muted-foreground">{field.descricao}</p>}</div>;
});

const TextAreaFieldComponent = React.memo(({ field, fieldValue, updateFormValue, readOnly, prefix = "" }: { field: any; fieldValue: string; updateFormValue: (slug: string, value: any) => void; readOnly: boolean; prefix?: string }) => {
  const [localValue, setLocalValue] = React.useState(fieldValue);
  const updateFormValueRef = React.useRef(updateFormValue);
  React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
  React.useEffect(() => { setLocalValue(fieldValue); }, [fieldValue]);
  return <div className="space-y-2 col-span-4"><Label htmlFor={`form-${prefix}${field.id}`}>{field.nome}{field.obrigatorio && <span className="text-destructive"> *</span>}</Label><Textarea id={`form-${prefix}${field.id}`} value={localValue} onChange={(e) => setLocalValue(e.target.value)} onBlur={() => updateFormValueRef.current(field.slug, localValue)} placeholder={field.placeholder || ""} disabled={readOnly} rows={3} />{field.descricao && <p className="text-xs text-muted-foreground">{field.descricao}</p>}</div>;
});

const NumberFieldComponent = React.memo(({ field, fieldValue, updateFormValue, readOnly, prefix = "" }: { field: any; fieldValue: string; updateFormValue: (slug: string, value: any) => void; readOnly: boolean; prefix?: string }) => {
  const [localValue, setLocalValue] = React.useState(fieldValue);
  const updateFormValueRef = React.useRef(updateFormValue);
  React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
  React.useEffect(() => { setLocalValue(fieldValue); }, [fieldValue]);
  return <div className="space-y-2"><Label htmlFor={`form-${prefix}${field.id}`}>{field.nome}{field.obrigatorio && <span className="text-destructive"> *</span>}</Label><Input id={`form-${prefix}${field.id}`} type="number" value={localValue} onChange={(e) => setLocalValue(e.target.value)} onBlur={() => updateFormValueRef.current(field.slug, localValue)} placeholder={field.placeholder || ""} disabled={readOnly} />{field.descricao && <p className="text-xs text-muted-foreground">{field.descricao}</p>}</div>;
});

export const SaleItemsTable = forwardRef<SaleItemsTableHandle, SaleItemsTableProps>(
function SaleItemsTableComponent({ items, onItemsChange, onProductSelectOpenChange, readOnly = false }: SaleItemsTableProps, ref) {
  const { calculatePrice } = useUnitConversion();
  const [isDesktop, setIsDesktop] = useState(false);
  const [productSelectOpenIndex, setProductSelectOpenIndex] = useState<number | null>(null);
  const [tempProductLabelByRow, setTempProductLabelByRow] = useState<Record<number, string>>({});
  const qtyRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [productProfile, setProductProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);

  const { data: products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("status", "ativo")
        .order("nome");
      if (error) throw error;
      return (data || []) as unknown as Product[];
    },
  });

  // Detecta breakpoint (md: >= 768px) para renderizar apenas um layout
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    try { mq.addEventListener('change', update); } catch { mq.addListener(update); }
    return () => {
      try { mq.removeEventListener('change', update); } catch { mq.removeListener(update); }
    };
  }, []);

  const { data: unitMeasurements } = useQuery({
    queryKey: ["unit_measurements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("unit_measurements" as any)
        .select("*")
        .order("nome", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as UnitMeasurement[];
    },
  });

  const calculateSubtotal = (quantidade: number, preco_unitario: number, desconto: number) => {
    return (preco_unitario - desconto) * quantidade;
  };

  const bumpQty = (index: number, delta: number) => {
    const current = items[index]?.quantidade ?? 0;
    const productId = items[index]?.product_id;
    const decimalPlaces = productId ? getQuantityDecimalPlaces(productId) : 0;
    const step = decimalPlaces === 0 ? 1 : Math.pow(10, -decimalPlaces);
    const next = Math.max(0, parseFloat((current + delta * step).toFixed(decimalPlaces)));
    if (readOnly) return;
    updateItem(index, 'quantidade', next);
  };

  // Foca e seleciona a quantidade na linha indicada (padrão última linha com item)
  const focusQuantity = (index: number = items.length - 1) => {
    const ref = qtyRefs.current[index];
    if (ref) {
      ref.focus();
      try { ref.select(); } catch {}
    }
  };

  // Expõe handlers imperativos para o modal controlar abertura de selects e foco
  useImperativeHandle(ref, () => ({
    openNewRowProductSelect: () => setProductSelectOpenIndex(items.length),
    focusQuantity,
    closeProductSelect: () => setProductSelectOpenIndex(null),
  }), [items.length]);

  // Notifica o pai quando o select de produto abrir/fechar
  React.useEffect(() => {
    onProductSelectOpenChange?.(productSelectOpenIndex !== null);
  }, [productSelectOpenIndex, onProductSelectOpenChange]);

  const handleProductChange = (
    productId: string,
    index: number,
    productHint?: { nome?: string; preco_venda?: number; unit_measurement_purchase_id?: string | null; unit_measurement_sale_id?: string | null }
  ) => {
    if (readOnly) return;
    const product = products?.find((p) => p.id === productId);
    const effectiveName = product?.nome ?? productHint?.nome ?? "";
    if (product || productHint) {
      // Calcula o preço unitário considerando conversão de unidades
      let precoUnitario = Number(product?.preco_venda ?? productHint?.preco_venda ?? 0);
      
      // Se o produto tem unidades configuradas, usa o preço de compra como base
      if ((product?.unit_measurement_purchase_id || productHint?.unit_measurement_purchase_id) && (product?.unit_measurement_sale_id || productHint?.unit_measurement_sale_id)) {
        const purchaseUnit = unitMeasurements?.find(u => u.id === (product?.unit_measurement_purchase_id ?? productHint?.unit_measurement_purchase_id!));
        const saleUnit = unitMeasurements?.find(u => u.id === (product?.unit_measurement_sale_id ?? productHint?.unit_measurement_sale_id!));
        
        if (purchaseUnit && saleUnit && purchaseUnit.slug !== saleUnit.slug) {
          // Calcula o preço para 1 unidade de venda
          const convertedPrice = calculatePrice(
            Number(product?.preco_venda ?? productHint?.preco_venda ?? 0),
            1,
            purchaseUnit.slug,
            saleUnit.slug,
            product?.unit_measurement_conversions || undefined
          );
          
          if (convertedPrice !== null) {
            precoUnitario = convertedPrice;
          }
        }
      }
      
      const newItem: SaleItem = {
        product_id: productId,
        quantidade: 1,
        preco_unitario: precoUnitario,
        desconto: 0,
        total: precoUnitario,
      };
      
      const newItems = [...items];
      newItems[index] = newItem;
      onItemsChange(newItems);
      setProductSelectOpenIndex(null);
      // Remove label temporário desta linha (se existir)
      setTempProductLabelByRow((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      // Foca a quantidade após renderizar a nova linha com o produto
      requestAnimationFrame(() => focusQuantity(index));
    }
  };

  const updateItem = (index: number, field: keyof SaleItem, value: number) => {
    if (readOnly) return;
    const newItems = [...items];
    newItems[index] = { 
      ...newItems[index], 
      [field]: value,
      total: calculateSubtotal(
        field === 'quantidade' ? value : newItems[index].quantidade,
        field === 'preco_unitario' ? value : newItems[index].preco_unitario,
        field === 'desconto' ? value : newItems[index].desconto
      )
    };
    onItemsChange(newItems);
  };

  const removeItem = (index: number) => {
    if (readOnly) return;
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const getProductName = (productId: string) => {
    return products?.find((p) => p.id === productId)?.nome || "";
  };

  const getSaleUnitSlug = (productId: string) => {
    const product = products?.find((p) => p.id === productId);
    if (!product?.unit_measurement_sale_id) return null;
    
    const unit = unitMeasurements?.find(u => u.id === product.unit_measurement_sale_id);
    return unit?.slug || null;
  };

  const getPurchaseUnitSlug = (productId: string) => {
    const product = products?.find((p) => p.id === productId);
    if (!product?.unit_measurement_purchase_id) return null;

    const unit = unitMeasurements?.find(u => u.id === product.unit_measurement_purchase_id);
    return unit?.slug || null;
  };

  const getQuantityDecimalPlaces = (productId: string): number => {
    const product = products?.find((p) => p.id === productId);
    return product?.quantity_decimal_places ?? 0;
  };

  const getProductFormId = (productId: string): string | null => {
    const product = products?.find((p) => p.id === productId);
    return product?.form_id ?? null;
  };

  // Componente para renderizar campos do formulário (versão desktop)
  // Componente para campos de texto que usa estado local para evitar perda de foco
  const TextFieldComponent = React.memo(({ 
    field, 
    fieldValue, 
    updateFormValue, 
    readOnly,
    prefix = ""
  }: { 
    field: any; 
    fieldValue: string; 
    updateFormValue: (slug: string, value: any) => void; 
    readOnly: boolean;
    prefix?: string;
  }) => {
    const [localValue, setLocalValue] = React.useState(fieldValue);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const updateFormValueRef = React.useRef(updateFormValue);
    const isFocusedRef = React.useRef(false);
    const fieldValueRef = React.useRef(fieldValue);

    // Atualizar ref quando updateFormValue mudar
    React.useEffect(() => {
      updateFormValueRef.current = updateFormValue;
    }, [updateFormValue]);

    // Atualizar ref do fieldValue
    React.useEffect(() => {
      fieldValueRef.current = fieldValue;
    }, [fieldValue]);

    // Sincronizar com valor externo quando mudar (mas não quando estamos editando)
    React.useEffect(() => {
      // Só atualizar se não estiver focado e o valor realmente mudou
      if (!isFocusedRef.current && fieldValueRef.current !== fieldValue) {
        setLocalValue(fieldValue);
      }
    }, [fieldValue]);

    const handleFocus = React.useCallback(() => {
      isFocusedRef.current = true;
    }, []);

    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      
      // NÃO atualizar estado pai durante a digitação
      // Apenas atualizar no onBlur para evitar re-renderizações
    }, []);

    const handleBlur = React.useCallback(() => {
      isFocusedRef.current = false;
      // Atualizar estado pai imediatamente ao sair do campo
      updateFormValueRef.current(field.slug, localValue);
    }, [field.slug, localValue]);

    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className="space-y-2">
        <Label htmlFor={`form-${prefix}${field.id}`}>
          {field.nome}
          {field.obrigatorio && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id={`form-${prefix}${field.id}`}
          value={localValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={field.placeholder || ""}
          disabled={readOnly}
        />
        {field.descricao && (
          <p className="text-xs text-muted-foreground">{field.descricao}</p>
        )}
      </div>
    );
  }, (prevProps, nextProps) => {
    // Comparação customizada: só re-renderiza se field ou readOnly mudarem
    // Ignora mudanças em fieldValue e updateFormValue durante a digitação
    return (
      prevProps.field.id === nextProps.field.id &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.prefix === nextProps.prefix &&
      prevProps.field.nome === nextProps.field.nome &&
      prevProps.field.obrigatorio === nextProps.field.obrigatorio &&
      prevProps.field.placeholder === nextProps.field.placeholder &&
      prevProps.field.descricao === nextProps.field.descricao
    );
  });

  const FormFieldsRow = React.memo(({ item, index }: { item: SaleItem; index: number }) => {
    const formId = item.product_id ? getProductFormId(item.product_id) : null;
    const { data: productById } = useQuery({
      queryKey: ["product-by-id-for-form", item.product_id],
      enabled: !!item.product_id && !formId,
      queryFn: async () => {
        const { data, error } = await supabase
          .from("products")
          .select("id, form_id")
          .eq("id", item.product_id)
          .maybeSingle();
        if (error) throw error;
        return data as { id: string; form_id: string | null } | null;
      },
      staleTime: 0,
    });
    const effectiveFormId = formId || productById?.form_id || null;
    
    // Usar ref para items e onItemsChange para evitar recriação da função
    // IMPORTANTE: Hooks devem ser chamados antes de qualquer return condicional
    const itemsRef = useRef(items);
    const onItemsChangeRef = useRef(onItemsChange);
    
    React.useEffect(() => {
      itemsRef.current = items;
      onItemsChangeRef.current = onItemsChange;
    }, [items, onItemsChange]);
    
    const { data: formData, isLoading } = useFormWithFields(effectiveFormId || "", {
      enabled: !!effectiveFormId && !!item.product_id,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
    });

    // Apenas buscar relacionamentos de opções se houver campos que têm opções
    const hasFieldsWithOptions = useMemo(() => {
      const normalize = (t: string) => {
        const s = (t || "").toLowerCase();
        if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
        if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
        if (["radio"].includes(s)) return "radio";
        if (["checkbox"].includes(s)) return "checkbox";
        return s;
      };
      return formData?.fields?.some((f: any) => ["select", "multiselect", "radio", "checkbox"].includes(normalize(f.tipo))) ?? false;
    }, [formData?.fields]);

    // Calcular IDs de opções apenas quando necessário
    const allOptionIds = useMemo(() => {
      if (!formData?.fields || !hasFieldsWithOptions) return [];
      const normalize = (t: string) => {
        const s = (t || "").toLowerCase();
        if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
        if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
        if (["radio"].includes(s)) return "radio";
        if (["checkbox"].includes(s)) return "checkbox";
        return s;
      };
      return formData.fields
        .filter((f: any) => ["select", "multiselect", "radio", "checkbox"].includes(normalize(f.tipo)))
        .flatMap((f: any) => {
          const flatten = (opts: any[]): string[] => {
            const ids: string[] = [];
            for (const opt of opts || []) {
              ids.push(opt.id);
              if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
                ids.push(...flatten(opt.sub_opcoes));
              }
            }
            return ids;
          };
          return flatten(f.options || []);
        });
    }, [formData?.fields, hasFieldsWithOptions]);

    // Buscar relacionamentos entre opções (consulta única quando necessário)
    const { data: optionRelations = [] } = useQuery({
      queryKey: ["form-option-relations", effectiveFormId, allOptionIds.sort().join(",")],
      enabled:
        !!effectiveFormId &&
        !!formData?.fields &&
        hasFieldsWithOptions &&
        !isLoading &&
        allOptionIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutos - dados não ficam "stale" por 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos - cache mantido por 10 minutos
      queryFn: async () => {
        if (allOptionIds.length === 0) return [];

        const { data, error } = await supabase
          .from("form_field_option_relations")
          .select("*")
          .in("option_id", allOptionIds);

        if (error && error.code !== "PGRST116") {
          console.error("Erro ao buscar relacionamentos:", error);
          return [];
        }

        return data || [];
      },
    });

    if (!effectiveFormId || !formData?.fields || formData.fields.length === 0) {
      return null;
    }

    const updateFormValue = useCallback((fieldSlug: string, value: any) => {
      if (readOnly) return;
      const newItems = [...itemsRef.current];
      const currentFormValues = newItems[index].form_values || {};
      
      const field = formData?.fields?.find((f: any) => f.slug === fieldSlug);
      
      // Apenas processar dependências para campos que podem ter dependências
      // (select, multiselect, radio) - campos de texto não têm dependências
      if (field && ["select", "multiselect", "radio"].includes(field.tipo)) {
        const dependentFields = formData.dependencies
          .filter((d: any) => d.depends_on_field_id === field.id)
          .map((d: any) => formData.fields.find((f: any) => f.id === d.field_id))
          .filter(Boolean);

        const updatedFormValues = { ...currentFormValues, [fieldSlug]: value };
        
        // Limpar valores dos campos dependentes
        dependentFields.forEach((depField: any) => {
          if (depField) {
            delete updatedFormValues[depField.slug];
          }
        });

        newItems[index] = {
          ...newItems[index],
          form_values: updatedFormValues,
        };
      } else {
        // Para campos de texto e outros tipos, apenas atualizar o valor
        newItems[index] = {
          ...newItems[index],
          form_values: {
            ...currentFormValues,
            [fieldSlug]: value,
          },
        };
      }
      
      onItemsChangeRef.current(newItems);
    }, [readOnly, index, formData]);

    // Usar ref para item para evitar recriação de getFieldValue
    const itemRef = useRef(item);
    React.useEffect(() => {
      itemRef.current = item;
    }, [item]);

    const getFieldValue = useCallback((fieldSlug: string) => {
      return itemRef.current.form_values?.[fieldSlug] ?? "";
    }, []);

    // Função para filtrar opções baseado em dependências
    const getFilteredOptions = (field: any) => {
      if (!field.options || field.options.length === 0) {
        return [];
      }

      const dependency = formData.dependencies.find((d: any) => d.field_id === field.id);
      
      // Se não há dependência, retornar todas as opções
      if (!dependency) {
        const flatten = (opts: any[]): any[] => {
          const result: any[] = [];
          for (const opt of opts || []) {
            const { sub_opcoes, ...optWithoutSubs } = opt;
            result.push(optWithoutSubs);
            if (sub_opcoes && sub_opcoes.length > 0) {
              result.push(...flatten(sub_opcoes));
            }
          }
          return result;
        };
        return flatten(field.options);
      }

      const dependentField = formData.fields.find((f: any) => f.id === dependency.depends_on_field_id);
      if (!dependentField) {
        return [];
      }

      const dependentFieldValue = getFieldValue(dependentField.slug);
      if (dependentFieldValue === undefined || dependentFieldValue === null || dependentFieldValue === "") {
        return [];
      }

      // Buscar a opção selecionada no campo dependente
      const flatten = (opts: any[]): any[] => {
        const result: any[] = [];
        for (const opt of opts || []) {
          const { sub_opcoes, ...optWithoutSubs } = opt;
          result.push(optWithoutSubs);
          if (sub_opcoes && sub_opcoes.length > 0) {
            result.push(...flatten(sub_opcoes));
          }
        }
        return result;
      };

      const dependentFieldOptionsFlat = flatten(dependentField.options || []);
      const valuesArray = Array.isArray(dependentFieldValue) ? dependentFieldValue : [dependentFieldValue];
      const selectedParentOptions = valuesArray
        .map((val: any) => dependentFieldOptionsFlat.find((opt: any) => opt.valor === val))
        .filter(Boolean);

      if (selectedParentOptions.length === 0) {
        return [];
      }

      // Buscar relacionamentos many-to-many acumulando todas as seleções
      const relatedOptionIdsSet = new Set<string>();
      optionRelations
        .filter((rel: any) => selectedParentOptions.some((sel: any) => rel.related_option_id === sel.id))
        .forEach((rel: any) => relatedOptionIdsSet.add(rel.option_id));

      if (relatedOptionIdsSet.size > 0) {
        const allOptionsFlat = flatten(field.options);
        return allOptionsFlat.filter((opt: any) => relatedOptionIdsSet.has(opt.id));
      }

      // Fallback: usar lógica hierárquica (sub-opções)
      const findOptionInTree = (opts: any[], valor: string): any => {
        for (const opt of opts) {
          if (opt.valor === valor) {
            return opt;
          }
          if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
            const found = findOptionInTree(opt.sub_opcoes, valor);
            if (found) return found;
          }
        }
        return null;
      };

      const selectedOptionsHier = valuesArray
        .map((val: any) => findOptionInTree(dependentField.options || [], val))
        .filter((opt: any) => opt && opt.sub_opcoes && opt.sub_opcoes.length > 0)
        .flatMap((opt: any) => opt.sub_opcoes);
      if (selectedOptionsHier.length > 0) {
        return selectedOptionsHier;
      }

      return [];
    };

    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30">
            <div className="text-sm text-muted-foreground py-2">Carregando formulário...</div>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow>
        <TableCell colSpan={5} className="bg-muted/30 p-4 border-2 rounded-md">
          <div className="grid grid-cols-4 gap-4">
              {formData.fields.map((field: any) => {
                const fieldValue = getFieldValue(field.slug);

                const normalizeType = (t: string) => {
                  const s = (t || "").toLowerCase();
                  if (["área de texto", "area de texto", "textarea"].includes(s)) return "textarea";
                  if (["texto", "text"].includes(s)) return "text";
                  if (["número", "numero", "number"].includes(s)) return "number";
                  if (["data e hora", "datetime", "datahora"].includes(s)) return "datetime";
                  if (["data", "date"].includes(s)) return "date";
                  if (["e-mail", "email"].includes(s)) return "email";
                  if (["telefone", "tel", "phone"].includes(s)) return "tel";
                  if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
                  if (["múltipla seleção", "multipla selecao", "multiplas seleções", "multiplas selecao", "multiselect"].includes(s)) return "multiselect";
                  if (["checkbox"].includes(s)) return "checkbox";
                  if (["radio"].includes(s)) return "radio";
                  if (["arquivos", "arquivo", "files", "file"].includes(s)) return "file";
                  if (["imagens", "imagem", "images", "image"].includes(s)) return "image";
                  return s;
                };

                switch (normalizeType(field.tipo)) {
                  case "text":
                  case "email":
                  case "tel":
                    return (
                      <TextFieldComponent
                        key={field.id}
                        field={field}
                        fieldValue={fieldValue}
                        updateFormValue={updateFormValue}
                        readOnly={readOnly}
                      />
                    );
                  
                  case "textarea":
                    return (
                      <TextAreaFieldComponent
                        key={field.id}
                        field={field}
                        fieldValue={fieldValue}
                        updateFormValue={updateFormValue}
                        readOnly={readOnly}
                      />
                    );
                  
                  case "number":
                    return (
                      <NumberFieldComponent
                        key={field.id}
                        field={field}
                        fieldValue={fieldValue}
                        updateFormValue={updateFormValue}
                        readOnly={readOnly}
                      />
                    );
                  
                  case "date":
                    return (
                      <div key={field.id} className="space-y-2">
                        <Label htmlFor={`form-${field.id}`}>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <DateField
                          id={`form-${field.id}`}
                          value={fieldValue || ""}
                          onChange={(e) => updateFormValue(field.slug, (e.target as HTMLInputElement).value)}
                          disabled={readOnly}
                        />
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );

                  case "datetime":
                    return (
                      <div key={field.id} className="space-y-2">
                        <Label htmlFor={`form-${field.id}`}>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <Input
                          id={`form-${field.id}`}
                          type="datetime-local"
                          value={fieldValue || ""}
                          onChange={(e) => updateFormValue(field.slug, e.target.value)}
                          disabled={readOnly}
                        />
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );
                  
                  case "select":
                    const filteredOptions = getFilteredOptions(field);
                    const hasDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                    return (
                      <div key={field.id} className="space-y-2">
                        <Label htmlFor={`form-${field.id}`}>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <Select
                          value={fieldValue || ""}
                          onValueChange={(value) => updateFormValue(field.slug, value)}
                          disabled={readOnly || (hasDependency && filteredOptions.length === 0)}
                        >
                          <SelectTrigger id={`form-${field.id}`}>
                            <SelectValue placeholder={hasDependency && filteredOptions.length === 0 ? "Selecione o campo anterior primeiro" : (field.placeholder || "Selecione...")} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredOptions.length > 0 ? (
                              filteredOptions.map((option: any) => (
                                <SelectItem key={option.id} value={option.valor}>
                                  {option.label}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="__no_options__" disabled>
                                {hasDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );
                  
                  case "multiselect":
                    const multiOptions = getFilteredOptions(field);
                    const hasMultiDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                    const multiValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
                    return (
                      <div key={field.id} className="space-y-2">
                        <Label>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <div className="space-y-2">
                          {multiOptions.length > 0 ? (
                            multiOptions.map((option: any) => (
                              <div key={option.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`multiselect-${option.id}`}
                                  checked={multiValues.includes(option.valor)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      updateFormValue(field.slug, [...multiValues, option.valor]);
                                    } else {
                                      updateFormValue(field.slug, multiValues.filter((v: string) => v !== option.valor));
                                    }
                                  }}
                                  disabled={readOnly || (hasMultiDependency && multiOptions.length === 0)}
                                />
                                <Label htmlFor={`multiselect-${option.id}`} className="font-normal cursor-pointer">
                                  {option.label}
                                </Label>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              {hasMultiDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                            </div>
                          )}
                        </div>
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );
                  
                  case "radio":
                    const filteredRadioOptions = getFilteredOptions(field);
                    const hasRadioDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                    return (
                      <div key={field.id} className="space-y-2">
                        <Label>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <RadioGroup
                          value={fieldValue || ""}
                          onValueChange={(value) => updateFormValue(field.slug, value)}
                          disabled={readOnly || (hasRadioDependency && filteredRadioOptions.length === 0)}
                        >
                          {filteredRadioOptions.length > 0 ? (
                            filteredRadioOptions.map((option: any) => (
                              <div key={option.id} className="flex items-center space-x-2">
                                <RadioGroupItem value={option.valor} id={`radio-${option.id}`} />
                                <Label htmlFor={`radio-${option.id}`} className="font-normal cursor-pointer">
                                  {option.label}
                                </Label>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              {hasRadioDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                            </div>
                          )}
                        </RadioGroup>
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );
                  
                  case "checkbox":
                    const filteredCheckboxOptions = getFilteredOptions(field);
                    const hasCheckboxDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
                    const checkedValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
                    return (
                      <div key={field.id} className="space-y-2">
                        <Label>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <div className="space-y-2">
                          {filteredCheckboxOptions.length > 0 ? (
                            filteredCheckboxOptions.map((option: any) => (
                              <div key={option.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`checkbox-${option.id}`}
                                  checked={checkedValues.includes(option.valor)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      updateFormValue(field.slug, [...checkedValues, option.valor]);
                                    } else {
                                      updateFormValue(field.slug, checkedValues.filter((v: string) => v !== option.valor));
                                    }
                                  }}
                                  disabled={readOnly}
                                />
                                <Label htmlFor={`checkbox-${option.id}`} className="font-normal cursor-pointer">
                                  {option.label}
                                </Label>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              {hasCheckboxDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                            </div>
                          )}
                        </div>
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );
                  
                  case "file":
                  case "image":
                    const isImage = field.tipo === "image";
                    return (
                      <div key={field.id} className="space-y-2 col-span-2">
                        <Label htmlFor={`form-${field.id}`}>
                          {field.nome}
                          {field.obrigatorio && <span className="text-destructive"> *</span>}
                        </Label>
                        <Input
                          id={`form-${field.id}`}
                          type="file"
                          accept={isImage ? "image/*" : "image/*,video/*,audio/*"}
                          disabled={readOnly}
                          onChange={async (e) => {
                            const file = e.target.files?.[0] || null;
                            if (!file) return;
                            const formData = new FormData();
                            formData.append("file", file);
                            try {
                              const { data, error } = await (supabase as any).functions.invoke("ai-upload-asset", { body: formData });
                              if (!error && data && data.path) {
                                const value = {
                                  bucket: data.bucket,
                                  path: data.path,
                                  signed_url: data.signed_url,
                                  mime_type: data.mime_type,
                                  media_type: data.media_type,
                                  original_name: file.name,
                                  uploaded_at: data.uploaded_at,
                                  expires_at: data.expires_at,
                                };
                                updateFormValue(field.slug, value);
                              }
                            } catch {}
                          }}
                        />
                        {fieldValue && typeof fieldValue === "object" && (fieldValue as any).signed_url && (
                          <div className="pt-1">
                            {isImage || (((fieldValue as any).mime_type || "").startsWith("image/")) ? (
                              <img src={(fieldValue as any).signed_url} alt={(fieldValue as any).original_name || "imagem"} className="h-16 w-auto rounded border" />
                            ) : (
                              <a href={(fieldValue as any).signed_url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline">
                                {(fieldValue as any).original_name || (fieldValue as any).path}
                              </a>
                            )}
                          </div>
                        )}
                        {field.descricao && (
                          <p className="text-xs text-muted-foreground">{field.descricao}</p>
                        )}
                      </div>
                    );
                  
                  default:
                    return null;
                }
              })}
          </div>
        </TableCell>
      </TableRow>
    );
  }, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renderizações desnecessárias
    // Só re-renderiza se o product_id mudar (não re-renderiza quando form_values mudam durante digitação)
    // Também verifica se o item em si mudou (exceto form_values)
    const prevItemWithoutFormValues = { ...prevProps.item };
    delete prevItemWithoutFormValues.form_values;
    const nextItemWithoutFormValues = { ...nextProps.item };
    delete nextItemWithoutFormValues.form_values;
    
    return (
      prevProps.item.product_id === nextProps.item.product_id &&
      prevProps.index === nextProps.index &&
      JSON.stringify(prevItemWithoutFormValues) === JSON.stringify(nextItemWithoutFormValues)
    );
  });

  // Adiciona uma linha vazia no final se não houver ou se a última linha já tem produto
  const displayItems = [...items];
  if (!readOnly) {
    if (displayItems.length === 0 || displayItems[displayItems.length - 1].product_id) {
      displayItems.push({
        product_id: "",
        quantidade: 1,
        preco_unitario: 0,
        desconto: 0,
        total: 0,
      });
    }
  }

  return (
    <div className="space-y-2">
      <h3 className="font-semibold">Itens da Venda</h3>
      {isDesktop && (
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[210px]">Produto</TableHead>
              <TableHead className="w-[130px] text-right">Preço</TableHead>
              <TableHead className="w-[160px] text-center">Qtd</TableHead>
              <TableHead className="w-[130px] text-right pr-1">Subtotal</TableHead>
              <TableHead className="w-[44px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayItems.map((item, index) => {
              const isNewRow = index >= items.length;
              
              return (
                <>
                <TableRow 
                  key={index}
                  className={!isNewRow ? "bg-gray-200 dark:bg-muted [&>td]:py-1 [&>th]:py-1" : "cursor-pointer"}
                  onClick={() => {
                    if (isNewRow) setProductSelectOpenIndex(index);
                  }}
                >
                  <TableCell className="w-[190px]">
                    {readOnly ? (
                      <div className="flex items-start gap-1">
                        <span className="text-sm line-clamp-2 flex-1 min-w-0">
                          {item.product_id ? (getProductName(item.product_id) || "-") : "-"}
                        </span>
                        {item.product_id && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProductProfile({ id: item.product_id, name: getProductName(item.product_id) || "produto" });
                                  }}
                                  className="ml-[3px] hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                  <FileText className="h-6 w-6 text-muted-foreground hover:text-blue-600 transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line z-[9999]" side="top" sideOffset={5}>
                                Ver ficha completa e relatórios{'\n'}do produto <strong>{getProductName(item.product_id) || 'produto'}</strong>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        {!isNewRow ? (
                          <>
                            <SearchableSelect
                              open={productSelectOpenIndex === index}
                              value={item.product_id}
                              onChange={(value) => handleProductChange(value, index)}
                              options={(products ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                              placeholder="Selecione o produto"
                              withBackdrop={false}
                              onOpenChange={(open) => {
                                setProductSelectOpenIndex((prev) => (open ? index : (prev === index ? null : prev)));
                              }}
                              valueLabel={tempProductLabelByRow[index]}
                              renderAddModal={index === displayItems.length - 1 ? ({ open, onOpenChange, onCreated }) => (
                                <QuickAddProductModal
                                  open={open}
                                  onOpenChange={onOpenChange}
                                  onProductCreated={({ id, nome, preco_venda, unit_measurement_purchase_id, unit_measurement_sale_id }) => {
                                    // Mostra imediatamente o nome no campo enquanto a lista atualiza
                                    setTempProductLabelByRow((prev) => ({ ...prev, [index]: nome }));
                                    handleProductChange(id, index, {
                                      nome,
                                      preco_venda,
                                      unit_measurement_purchase_id,
                                      unit_measurement_sale_id,
                                    });
                                    onCreated(id);
                                  }}
                                />
                              ) : undefined}
                            />
                            {item.product_id && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProductProfile({ id: item.product_id, name: getProductName(item.product_id) || "produto" });
                                      }}
                                      className="ml-[3px] hover:text-blue-600 transition-colors cursor-pointer"
                                    >
                                      <FileText className="h-6 w-6 text-muted-foreground hover:text-blue-600 transition-colors" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-pre-line">
                                    Ver ficha completa e relatórios{'\n'}do produto <strong>{getProductName(item.product_id) || 'produto'}</strong>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </>
                        ) : (
                          <SearchableSelect
                            value={item.product_id || ""}
                            open={productSelectOpenIndex === index}
                            onOpenChange={(open) => setProductSelectOpenIndex(open ? index : null)}
                            onChange={(value) => handleProductChange(value, index)}
                            options={(products ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                            placeholder="Selecione o produto"
                            withBackdrop={false}
                            valueLabel={tempProductLabelByRow[index]}
                            renderAddModal={index === displayItems.length - 1 ? ({ open, onOpenChange, onCreated }) => (
                              <QuickAddProductModal
                                open={open}
                                onOpenChange={onOpenChange}
                                onProductCreated={({ id, nome, preco_venda, unit_measurement_purchase_id, unit_measurement_sale_id }) => {
                                  setTempProductLabelByRow((prev) => ({ ...prev, [index]: nome }));
                                  handleProductChange(id, index, {
                                    nome,
                                    preco_venda,
                                    unit_measurement_purchase_id,
                                    unit_measurement_sale_id,
                                  });
                                  onCreated(id);
                                }}
                              />
                            ) : undefined}
                          />
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="w-[130px] text-right font-medium">
                    {item.product_id && !isNewRow ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block cursor-default">
                              {new Intl.NumberFormat("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(item.preco_unitario || 0)}
                              {getSaleUnitSlug(item.product_id) ? ` /${getSaleUnitSlug(item.product_id)}` : ""}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" align="end">
                            {(() => {
                              const product = products?.find(p => p.id === item.product_id);
                              const purchaseSlug = getPurchaseUnitSlug(item.product_id);
                              const base = product?.preco_venda ?? 0;
                              const formatted = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(base);
                              return <span>{formatted}{purchaseSlug ? ` /${purchaseSlug}` : ""}</span>;
                            })()}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell 
                    className="w-[160px]"
                    onClick={() => { if (isNewRow) setProductSelectOpenIndex(index); }}
                    onMouseDown={() => { if (isNewRow) setProductSelectOpenIndex(index); }}
                  >
                    <div className="flex items-center">
                      <div className="relative flex-1">
                        <DecimalInput
                          value={item.quantidade}
                          decimalPlaces={item.product_id ? getQuantityDecimalPlaces(item.product_id) : 0}
                          onFocus={(e) => {
                            // Seleciona todo o conteúdo ao focar
                            try { e.currentTarget.select(); } catch {}
                          }}
                          onClick={(e) => {
                            // Seleciona todo o conteúdo ao clicar
                            e.stopPropagation();
                            try { e.currentTarget.select(); } catch {}
                          }}
                          onMouseDown={(e) => {
                            // Evita que o mouse posicione o caret e remove a seleção
                            // Mantém o comportamento de selecionar tudo ao focar
                            const el = e.currentTarget;
                            if (document.activeElement !== el) {
                              e.preventDefault();
                              el.focus();
                              try { el.select(); } catch {}
                            }
                          }}
                          onValueChange={(value) => {
                            if (readOnly) return;
                            if (isNewRow || !item.product_id) return;
                            updateItem(index, 'quantidade', value);
                          }}
                          onBlur={(e) => {
                            if (readOnly) return;
                            if (isNewRow || !item.product_id) return;
                            // Garante que o valor seja atualizado mesmo se não houver mudança
                            const currentValue = item.quantidade ?? 0;
                            updateItem(index, 'quantidade', currentValue);
                          }}
                          disabled={readOnly || isNewRow || !item.product_id}
                          className={`pr-10 no-spinner ${(readOnly || isNewRow) ? "pointer-events-none" : ""}`}
                          ref={(el) => { qtyRefs.current[index] = el; }}
                        />
                        {!isNewRow && item.product_id && getSaleUnitSlug(item.product_id) && (
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-[55%] text-lg leading-none text-muted-foreground font-medium">
                            {getSaleUnitSlug(item.product_id)}
                          </span>
                        )}
                      </div>
                      {!readOnly && !isNewRow && item.product_id && (
                        <div className="ml-2 flex flex-col gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 text-muted-foreground hover:text-white outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                            onClick={(e) => { e.stopPropagation(); bumpQty(index, 1); }}
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0 text-muted-foreground hover:text-white outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                            onClick={(e) => { e.stopPropagation(); bumpQty(index, -1); }}
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-[130px] text-right font-semibold pr-1">
                    {item.product_id && !isNewRow
                      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(item.total)
                      : "-"}
                  </TableCell>
                  <TableCell className="w-[44px] text-right pr-2">
                    {!readOnly && !isNewRow && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="h-8 w-8 icon-chip-red"
                        data-testid="remove-item-button"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {!isNewRow && item.product_id && getProductFormId(item.product_id) && (
                  <FormFieldsRow key={`form-${item.product_id}-${index}`} item={item} index={index} />
                )}
              </>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}

      {!isDesktop && (
      <div className="space-y-2">
        <div className="space-y-3">
          {displayItems.map((item, index) => {
            const isNewRow = index >= items.length;

            return (
              <div
                key={`card-${index}`}
                className={`rounded-lg border ${!isNewRow ? "bg-muted/50" : "bg-background"} p-3 space-y-3 transition-colors`}
                onClick={() => {
                  if (isNewRow) setProductSelectOpenIndex(index);
                }}
                onMouseDown={() => {
                  if (isNewRow) setProductSelectOpenIndex(index);
                }}
              >
                <div className="flex gap-2 items-start">
                  <div className="flex-1 flex items-center gap-1">
                    {readOnly ? (
                      <>
                        <span className="text-sm line-clamp-2 flex-1 min-w-0">
                          {item.product_id ? (getProductName(item.product_id) || "-") : "-"}
                        </span>
                        {item.product_id && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProductProfile({ id: item.product_id, name: getProductName(item.product_id) || "produto" });
                                  }}
                                  className="ml-[3px] hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                  <FileText className="h-6 w-6 text-muted-foreground hover:text-blue-600 transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line z-[9999]" side="top" sideOffset={5}>
                                Ver ficha completa e relatórios{'\n'}do produto <strong>{getProductName(item.product_id) || 'produto'}</strong>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </>
                    ) : !isNewRow ? (
                      <>
                        <SearchableSelect
                          open={productSelectOpenIndex === index}
                          value={item.product_id}
                          onChange={(value) => handleProductChange(value, index)}
                          options={(products ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                          placeholder="Selecione o produto"
                          withBackdrop={false}
                          onOpenChange={(open) => {
                            setProductSelectOpenIndex(open ? index : null);
                          }}
                          valueLabel={tempProductLabelByRow[index]}
                          renderAddModal={index === displayItems.length - 1 ? ({ open, onOpenChange, onCreated }) => (
                            <QuickAddProductModal
                              open={open}
                              onOpenChange={onOpenChange}
                              onProductCreated={({ id, nome, preco_venda, unit_measurement_purchase_id, unit_measurement_sale_id }) => {
                                setTempProductLabelByRow((prev) => ({ ...prev, [index]: nome }));
                                handleProductChange(id, index, {
                                  nome,
                                  preco_venda,
                                  unit_measurement_purchase_id,
                                  unit_measurement_sale_id,
                                });
                                onCreated(id);
                              }}
                            />
                          ) : undefined}
                        />
                        {item.product_id && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProductProfile({ id: item.product_id, name: getProductName(item.product_id) || "produto" });
                                  }}
                                  className="ml-[3px] hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                  <FileText className="h-6 w-6 text-muted-foreground hover:text-blue-600 transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="whitespace-pre-line z-[9999]" side="top" sideOffset={5}>
                                Ver ficha completa e relatórios{'\n'}do produto <strong>{getProductName(item.product_id) || 'produto'}</strong>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </>
                    ) : (
                      <SearchableSelect
                        value={item.product_id || ""}
                        open={productSelectOpenIndex === index}
                        onOpenChange={(open) => setProductSelectOpenIndex(open ? index : null)}
                        onChange={(value) => handleProductChange(value, index)}
                        options={(products ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                        placeholder="Selecione o produto"
                        withBackdrop={false}
                        valueLabel={tempProductLabelByRow[index]}
                        renderAddModal={index === displayItems.length - 1 ? ({ open, onOpenChange, onCreated }) => (
                          <QuickAddProductModal
                            open={open}
                            onOpenChange={onOpenChange}
                            onProductCreated={({ id, nome, preco_venda, unit_measurement_purchase_id, unit_measurement_sale_id }) => {
                              setTempProductLabelByRow((prev) => ({ ...prev, [index]: nome }));
                              handleProductChange(id, index, {
                                nome,
                                preco_venda,
                                unit_measurement_purchase_id,
                                unit_measurement_sale_id,
                              });
                              onCreated(id);
                            }}
                          />
                        ) : undefined}
                      />
                    )}
                  </div>
                  <div className="min-w-[110px]">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Preço</div>
                    <div className="text-sm font-medium mt-1">
                      {item.product_id && !isNewRow ? (
                        <>
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(item.preco_unitario || 0)}
                          {getSaleUnitSlug(item.product_id) ? ` /${getSaleUnitSlug(item.product_id)}` : ""}
                        </>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground mb-1">Qtd</div>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <DecimalInput
                          value={item.quantidade}
                          decimalPlaces={item.product_id ? getQuantityDecimalPlaces(item.product_id) : 0}
                          onFocus={(e) => {
                            try { e.currentTarget.select(); } catch {}
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            try { e.currentTarget.select(); } catch {}
                          }}
                          onMouseDown={(e) => {
                            const el = e.currentTarget;
                            if (document.activeElement !== el) {
                              e.preventDefault();
                              el.focus();
                              try { el.select(); } catch {}
                            }
                          }}
                          onValueChange={(value) => {
                            if (readOnly) return;
                            if (isNewRow || !item.product_id) return;
                            updateItem(index, 'quantidade', value);
                          }}
                          onBlur={(e) => {
                            if (readOnly) return;
                            if (isNewRow || !item.product_id) return;
                            // Garante que o valor seja atualizado mesmo se não houver mudança
                            const currentValue = item.quantidade ?? 0;
                            updateItem(index, 'quantidade', currentValue);
                          }}
                          disabled={readOnly || isNewRow || !item.product_id}
                          className={`pr-9 no-spinner ${(readOnly || isNewRow) ? "pointer-events-none" : ""}`}
                          ref={(el) => { qtyRefs.current[index] = el; }}
                        />
                        {!isNewRow && item.product_id && getSaleUnitSlug(item.product_id) && (
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-[55%] text-sm leading-none text-muted-foreground font-medium">
                            {getSaleUnitSlug(item.product_id)}
                          </span>
                        )}
                      </div>
                      {!readOnly && !isNewRow && item.product_id && (
                        <div className="flex flex-col gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 p-0 text-muted-foreground"
                            onClick={(e) => { e.stopPropagation(); bumpQty(index, 1); }}
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 p-0 text-muted-foreground"
                            onClick={(e) => { e.stopPropagation(); bumpQty(index, -1); }}
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="min-w-[110px]">
                    <div className="text-xs text-muted-foreground mb-1">Subtotal</div>
                    <div className="text-sm font-semibold">
                      {item.product_id && !isNewRow
                        ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(item.total)
                        : "-"}
                    </div>
                  </div>

                  <div>
                    {!readOnly && !isNewRow && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                        className="h-8 w-8 icon-chip-red"
                        data-testid={`remove-item-card-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {!isNewRow && item.product_id && getProductFormId(item.product_id) && (
                  <div className="mt-3 pt-3 border-t">
                    <FormFieldsCard 
                      item={item} 
                      index={index}
                      formId={getProductFormId(item.product_id)}
                      items={items}
                      onItemsChange={onItemsChange}
                      readOnly={readOnly}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}
      {productProfile && (() => {
        const p = (products || []).find((x) => x.id === productProfile?.id);
        const infoItems = [
          p?.codigo_sku ? { value: `SKU: ${p.codigo_sku}` } : null,
          p?.product_category_id ? { value: "Ver categoria", badge: true } : null,
          p?.created_at ? { value: `Produto desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(p.created_at))}` } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!productProfile}
            onOpenChange={() => setProductProfile(null)}
            imageUrl={p?.foto_url || null}
            entityTypeLabel="Produto"
            entityName={p?.nome || productProfile.name}
            infoItems={infoItems}
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            onPeriodChange={(from, to) => { setRxFrom(from); setRxTo(to); }}
            renderContent={({ periodRange, setLoading }) => (
              productProfile ? <ProductDetailsView id={productProfile.id} embedded periodRange={periodRange} setLoading={setLoading} /> : null
            )}
          />
        );
      })()}
    </div>
  );
});

// Componente para renderizar campos do formulário na versão mobile
const FormFieldsCard = ({ 
  item, 
  index, 
  formId,
  items,
  onItemsChange,
  readOnly 
}: { 
  item: SaleItem; 
  index: number;
  formId: string | null;
  items: SaleItem[];
  onItemsChange: (items: SaleItem[]) => void;
  readOnly: boolean;
}) => {
  // Usar ref para items e onItemsChange para evitar recriação da função
  const itemsRef = useRef(items);
  const onItemsChangeRef = useRef(onItemsChange);
  
  React.useEffect(() => {
    itemsRef.current = items;
    onItemsChangeRef.current = onItemsChange;
  }, [items, onItemsChange]);

  // Usar ref para item para evitar recriação de getFieldValue
  const itemRef = useRef(item);
  React.useEffect(() => {
    itemRef.current = item;
  }, [item]);

  const { data: productByIdMobile } = useQuery({
    queryKey: ["product-by-id-for-form-mobile", item.product_id],
    enabled: !!item.product_id && !formId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, form_id")
        .eq("id", item.product_id)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; form_id: string | null } | null;
    },
    staleTime: 0,
  });

  const NumberFieldComponent = React.memo(({ 
    field, 
    fieldValue, 
    updateFormValue, 
    readOnly,
    prefix = ""
  }: { 
    field: any; 
    fieldValue: string; 
    updateFormValue: (slug: string, value: any) => void; 
    readOnly: boolean;
    prefix?: string;
  }) => {
    const [localValue, setLocalValue] = React.useState(fieldValue);
    const updateFormValueRef = React.useRef(updateFormValue);
    const isFocusedRef = React.useRef(false);
    const fieldValueRef = React.useRef(fieldValue);

    React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
    React.useEffect(() => { fieldValueRef.current = fieldValue; }, [fieldValue]);
    React.useEffect(() => { if (!isFocusedRef.current && fieldValueRef.current !== fieldValue) { setLocalValue(fieldValue); } }, [fieldValue]);

    const handleFocus = React.useCallback(() => { isFocusedRef.current = true; }, []);
    const handleBlur = React.useCallback(() => {
      isFocusedRef.current = false;
      updateFormValueRef.current(field.slug, localValue);
    }, [field.slug, localValue]);

    return (
      <div className="space-y-2">
        <Label htmlFor={`form-${prefix}${field.id}`}>
          {field.nome}
          {field.obrigatorio && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id={`form-${prefix}${field.id}`}
          type="number"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={field.placeholder || ""}
          disabled={readOnly}
        />
        {field.descricao && (
          <p className="text-xs text-muted-foreground">{field.descricao}</p>
        )}
      </div>
    );
  });

  const TextAreaFieldComponent = React.memo(({ 
    field, 
    fieldValue, 
    updateFormValue, 
    readOnly,
    prefix = ""
  }: { 
    field: any; 
    fieldValue: string; 
    updateFormValue: (slug: string, value: any) => void; 
    readOnly: boolean;
    prefix?: string;
  }) => {
    const [localValue, setLocalValue] = React.useState(fieldValue);
    const updateFormValueRef = React.useRef(updateFormValue);
    const isFocusedRef = React.useRef(false);
    const fieldValueRef = React.useRef(fieldValue);

    React.useEffect(() => { updateFormValueRef.current = updateFormValue; }, [updateFormValue]);
    React.useEffect(() => { fieldValueRef.current = fieldValue; }, [fieldValue]);
    React.useEffect(() => { if (!isFocusedRef.current && fieldValueRef.current !== fieldValue) { setLocalValue(fieldValue); } }, [fieldValue]);

    const handleFocus = React.useCallback(() => { isFocusedRef.current = true; }, []);
    const handleBlur = React.useCallback(() => {
      isFocusedRef.current = false;
      updateFormValueRef.current(field.slug, localValue);
    }, [field.slug, localValue]);

    return (
      <div className="space-y-2 col-span-4">
        <Label htmlFor={`form-${prefix}${field.id}`}>
          {field.nome}
          {field.obrigatorio && <span className="text-destructive"> *</span>}
        </Label>
        <Textarea
          id={`form-${prefix}${field.id}`}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={field.placeholder || ""}
          disabled={readOnly}
          rows={3}
        />
        {field.descricao && (
          <p className="text-xs text-muted-foreground">{field.descricao}</p>
        )}
      </div>
    );
  });
  const effectiveFormIdMobile = formId || productByIdMobile?.form_id || null;

  const { data: formData, isLoading } = useFormWithFields(effectiveFormIdMobile || "", {
    enabled: !!effectiveFormIdMobile && !!item.product_id,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
  });

  // Apenas buscar relacionamentos de opções se houver campos que têm opções
  const hasFieldsWithOptions = useMemo(() => {
    return formData?.fields?.some(
      (f: any) => ["select", "multiselect", "radio", "checkbox"].includes(f.tipo)
    ) ?? false;
  }, [formData?.fields]);

  // Calcular IDs de opções apenas quando necessário
  const allOptionIds = useMemo(() => {
    if (!formData?.fields || !hasFieldsWithOptions) return [];
    
    return formData.fields
      .filter((f: any) => ["select", "multiselect", "radio", "checkbox"].includes(f.tipo))
      .flatMap((f: any) => {
        const flatten = (opts: any[]): string[] => {
          const ids: string[] = [];
          for (const opt of opts || []) {
            ids.push(opt.id);
            if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
              ids.push(...flatten(opt.sub_opcoes));
            }
          }
          return ids;
        };
        return flatten(f.options || []);
      });
  }, [formData?.fields, hasFieldsWithOptions]);

  // Buscar relacionamentos entre opções
  const { data: optionRelations = [] } = useQuery({
    queryKey: ["form-option-relations-mobile", formId, allOptionIds.sort().join(",")],
    enabled: !!formId && !!formData?.fields && hasFieldsWithOptions && !isLoading && allOptionIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos - dados não ficam "stale" por 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos - cache mantido por 10 minutos
    queryFn: async () => {
      if (allOptionIds.length === 0) return [];

      const { data, error } = await supabase
        .from("form_field_option_relations")
        .select("*")
        .in("option_id", allOptionIds);

      if (error && error.code !== "PGRST116") {
        console.error("Erro ao buscar relacionamentos:", error);
        return [];
      }

      return data || [];
    },
  });

  if (!effectiveFormIdMobile || !formData?.fields || formData.fields.length === 0) {
    return null;
  }

  const getFieldValue = useCallback((fieldSlug: string) => {
    return itemRef.current.form_values?.[fieldSlug] ?? "";
  }, []);

  // Função para filtrar opções baseado em dependências
  const getFilteredOptions = (field: any) => {
    if (!field.options || field.options.length === 0) {
      return [];
    }

    const dependency = formData.dependencies.find((d: any) => d.field_id === field.id);
    
    // Se não há dependência, retornar todas as opções
    if (!dependency) {
      const flatten = (opts: any[]): any[] => {
        const result: any[] = [];
        for (const opt of opts || []) {
          const { sub_opcoes, ...optWithoutSubs } = opt;
          result.push(optWithoutSubs);
          if (sub_opcoes && sub_opcoes.length > 0) {
            result.push(...flatten(sub_opcoes));
          }
        }
        return result;
      };
      return flatten(field.options);
    }

    const dependentField = formData.fields.find((f: any) => f.id === dependency.depends_on_field_id);
    if (!dependentField) {
      return [];
    }

    const dependentFieldValue = getFieldValue(dependentField.slug);
    if (!dependentFieldValue) {
      return [];
    }

    // Buscar a opção selecionada no campo dependente
    const flatten = (opts: any[]): any[] => {
      const result: any[] = [];
      for (const opt of opts || []) {
        const { sub_opcoes, ...optWithoutSubs } = opt;
        result.push(optWithoutSubs);
        if (sub_opcoes && sub_opcoes.length > 0) {
          result.push(...flatten(sub_opcoes));
        }
      }
      return result;
    };

    const dependentFieldOptionsFlat = flatten(dependentField.options || []);
    const selectedParentOption = dependentFieldOptionsFlat.find(
      (opt: any) => opt.valor === dependentFieldValue
    );

    if (!selectedParentOption) {
      return [];
    }

    // Buscar relacionamentos many-to-many
    const relatedOptionIds = optionRelations
      .filter((rel: any) => rel.related_option_id === selectedParentOption.id)
      .map((rel: any) => rel.option_id);

    if (relatedOptionIds.length > 0) {
      const allOptionsFlat = flatten(field.options);
      return allOptionsFlat.filter((opt: any) => relatedOptionIds.includes(opt.id));
    }

    // Fallback: usar lógica hierárquica (sub-opções)
    const findOptionInTree = (opts: any[], valor: string): any => {
      for (const opt of opts) {
        if (opt.valor === valor) {
          return opt;
        }
        if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
          const found = findOptionInTree(opt.sub_opcoes, valor);
          if (found) return found;
        }
      }
      return null;
    };

    const selectedOption = findOptionInTree(dependentField.options || [], dependentFieldValue);
    if (selectedOption && selectedOption.sub_opcoes && selectedOption.sub_opcoes.length > 0) {
      return selectedOption.sub_opcoes;
    }

    return [];
  };

  // Função para atualizar valor e limpar dependentes
  const handleFormValueChange = useCallback((fieldSlug: string, value: any) => {
    if (readOnly) return;
    
    const field = formData?.fields?.find((f: any) => f.slug === fieldSlug);
    
    // Apenas processar dependências para campos que podem ter dependências
    // (select, multiselect, radio) - campos de texto não têm dependências
    if (field && ["select", "multiselect", "radio"].includes(field.tipo)) {
      const dependentFields = formData.dependencies
        .filter((d: any) => d.depends_on_field_id === field.id)
        .map((d: any) => formData.fields.find((f: any) => f.id === d.field_id))
        .filter(Boolean);

      const currentFormValues = { ...(itemRef.current.form_values || {}) };
      currentFormValues[fieldSlug] = value;
      
      // Limpar valores dos campos dependentes
      dependentFields.forEach((depField: any) => {
        if (depField) {
          delete currentFormValues[depField.slug];
        }
      });

      // Atualizar todos os valores de uma vez através de uma única chamada
      const newItems = [...itemsRef.current];
      newItems[index] = {
        ...newItems[index],
        form_values: currentFormValues,
      };
      onItemsChangeRef.current(newItems);
    } else {
      // Para campos de texto e outros tipos, apenas atualizar o valor
      const newItems = [...itemsRef.current];
      newItems[index] = {
        ...newItems[index],
        form_values: {
          ...(newItems[index].form_values || {}),
          [fieldSlug]: value,
        },
      };
      onItemsChangeRef.current(newItems);
    }
  }, [readOnly, index, formData]);

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">Carregando formulário...</div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-4">
        {formData.fields.map((field: any) => {
          const fieldValue = getFieldValue(field.slug);
          
            const normalizeType = (t: string) => {
              const s = (t || "").toLowerCase();
              if (["área de texto", "area de texto", "textarea"].includes(s)) return "textarea";
              if (["texto", "text"].includes(s)) return "text";
              if (["número", "numero", "number"].includes(s)) return "number";
              if (["data e hora", "datetime", "datahora"].includes(s)) return "datetime";
              if (["data", "date"].includes(s)) return "date";
              if (["e-mail", "email"].includes(s)) return "email";
              if (["telefone", "tel", "phone"].includes(s)) return "tel";
              if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
              if (["múltipla seleção", "multipla selecao", "multiplas seleções", "multiplas selecao", "multiselect"].includes(s)) return "multiselect";
              if (["checkbox"].includes(s)) return "checkbox";
              if (["radio"].includes(s)) return "radio";
              if (["arquivos", "arquivo", "files", "file"].includes(s)) return "file";
              if (["imagens", "imagem", "images", "image"].includes(s)) return "image";
              return s;
            };

            switch (normalizeType(field.tipo)) {
            case "text":
            case "email":
            case "tel":
              return (
                <TextFieldComponent
                  key={field.id}
                  field={field}
                  fieldValue={fieldValue}
                  updateFormValue={handleFormValueChange}
                  readOnly={readOnly}
                  prefix="mobile-"
                />
              );
            
            case "textarea":
              return (
                <TextAreaFieldComponent
                  key={field.id}
                  field={field}
                  fieldValue={fieldValue}
                  updateFormValue={handleFormValueChange}
                  readOnly={readOnly}
                  prefix="mobile-"
                />
              );
            
            case "number":
              return (
                <NumberFieldComponent
                  key={field.id}
                  field={field}
                  fieldValue={fieldValue}
                  updateFormValue={handleFormValueChange}
                  readOnly={readOnly}
                  prefix="mobile-"
                />
              );
            
            case "date":
              return (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={`form-mobile-${field.id}`}>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <DateField
                    id={`form-mobile-${field.id}`}
                    value={fieldValue || ""}
                    onChange={(e) => handleFormValueChange(field.slug, (e.target as HTMLInputElement).value)}
                    disabled={readOnly}
                  />
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );

            case "datetime":
              return (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={`form-mobile-${field.id}`}>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <Input
                    id={`form-mobile-${field.id}`}
                    type="datetime-local"
                    value={fieldValue || ""}
                    onChange={(e) => handleFormValueChange(field.slug, e.target.value)}
                    disabled={readOnly}
                  />
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );
            
            case "select":
              const filteredMobileOptions = getFilteredOptions(field);
              const hasMobileDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
              return (
                <div key={field.id} className="space-y-2">
                  <Label htmlFor={`form-mobile-${field.id}`}>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <Select
                    value={fieldValue || ""}
                    onValueChange={(value) => handleFormValueChange(field.slug, value)}
                    disabled={readOnly || (hasMobileDependency && filteredMobileOptions.length === 0)}
                  >
                    <SelectTrigger id={`form-mobile-${field.id}`}>
                      <SelectValue placeholder={hasMobileDependency && filteredMobileOptions.length === 0 ? "Selecione o campo anterior primeiro" : (field.placeholder || "Selecione...")} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredMobileOptions.length > 0 ? (
                        filteredMobileOptions.map((option: any) => (
                          <SelectItem key={option.id} value={option.valor}>
                            {option.label}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="__no_options__" disabled>
                          {hasMobileDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );
            
            case "multiselect":
              const filteredMobileMultiOptions = getFilteredOptions(field);
              const hasMobileMultiDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
              const checkedMobileMultiValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
              return (
                <div key={field.id} className="space-y-2">
                  <Label>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <div className="space-y-2">
                    {filteredMobileMultiOptions.length > 0 ? (
                      filteredMobileMultiOptions.map((option: any) => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`multiselect-mobile-${option.id}`}
                            checked={checkedMobileMultiValues.includes(option.valor)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFormValueChange(field.slug, [...checkedMobileMultiValues, option.valor]);
                              } else {
                                handleFormValueChange(field.slug, checkedMobileMultiValues.filter((v: string) => v !== option.valor));
                              }
                            }}
                            disabled={readOnly || (hasMobileMultiDependency && filteredMobileMultiOptions.length === 0)}
                          />
                          <Label htmlFor={`multiselect-mobile-${option.id}`} className="font-normal cursor-pointer">
                            {option.label}
                          </Label>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {hasMobileMultiDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                      </div>
                    )}
                  </div>
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );
            
            case "radio":
              const filteredMobileRadioOptions = getFilteredOptions(field);
              const hasMobileRadioDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
              return (
                <div key={field.id} className="space-y-2">
                  <Label>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <RadioGroup
                    value={fieldValue || ""}
                    onValueChange={(value) => handleFormValueChange(field.slug, value)}
                    disabled={readOnly || (hasMobileRadioDependency && filteredMobileRadioOptions.length === 0)}
                  >
                    {filteredMobileRadioOptions.length > 0 ? (
                      filteredMobileRadioOptions.map((option: any) => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.valor} id={`radio-mobile-${option.id}`} />
                          <Label htmlFor={`radio-mobile-${option.id}`} className="font-normal cursor-pointer">
                            {option.label}
                          </Label>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {hasMobileRadioDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                      </div>
                    )}
                  </RadioGroup>
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );
            
            case "checkbox":
              const filteredMobileCheckboxOptions = getFilteredOptions(field);
              const hasMobileCheckboxDependency = formData.dependencies.some((d: any) => d.field_id === field.id);
              const checkedMobileValues = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
              return (
                <div key={field.id} className="space-y-2">
                  <Label>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <div className="space-y-2">
                    {filteredMobileCheckboxOptions.length > 0 ? (
                      filteredMobileCheckboxOptions.map((option: any) => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`checkbox-mobile-${option.id}`}
                            checked={checkedMobileValues.includes(option.valor)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                handleFormValueChange(field.slug, [...checkedMobileValues, option.valor]);
                              } else {
                                handleFormValueChange(field.slug, checkedMobileValues.filter((v: string) => v !== option.valor));
                              }
                            }}
                            disabled={readOnly}
                          />
                          <Label htmlFor={`checkbox-mobile-${option.id}`} className="font-normal cursor-pointer">
                            {option.label}
                          </Label>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        {hasMobileCheckboxDependency ? "Selecione o campo anterior primeiro" : "Nenhuma opção disponível"}
                      </div>
                    )}
                  </div>
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );
            
            case "file":
            case "image":
              const isMobileImage = field.tipo === "image";
              return (
                <div key={field.id} className="space-y-2 col-span-4">
                  <Label htmlFor={`form-mobile-${field.id}`}>
                    {field.nome}
                    {field.obrigatorio && <span className="text-destructive"> *</span>}
                  </Label>
                  <Input
                    id={`form-mobile-${field.id}`}
                    type="file"
                    accept={isMobileImage ? "image/*" : "image/*,video/*,audio/*"}
                    disabled={readOnly}
                    onChange={async (e) => {
                      const file = e.target.files?.[0] || null;
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const { data, error } = await (supabase as any).functions.invoke("ai-upload-asset", { body: formData });
                        if (!error && data && data.path) {
                          const value = {
                            bucket: data.bucket,
                            path: data.path,
                            signed_url: data.signed_url,
                            mime_type: data.mime_type,
                            media_type: data.media_type,
                            original_name: file.name,
                            uploaded_at: data.uploaded_at,
                            expires_at: data.expires_at,
                          };
                          handleFormValueChange(field.slug, value);
                        }
                      } catch {}
                    }}
                  />
                  {fieldValue && typeof fieldValue === "object" && (fieldValue as any).signed_url && (
                    <div className="pt-1">
                      {isMobileImage || (((fieldValue as any).mime_type || "").startsWith("image/")) ? (
                        <img src={(fieldValue as any).signed_url} alt={(fieldValue as any).original_name || "imagem"} className="h-20 w-auto rounded border" />
                      ) : (
                        <a href={(fieldValue as any).signed_url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline">
                          {(fieldValue as any).original_name || (fieldValue as any).path}
                        </a>
                      )}
                    </div>
                  )}
                  {field.descricao && (
                    <p className="text-xs text-muted-foreground">{field.descricao}</p>
                  )}
                </div>
              );
            
            default:
              return null;
          }
        })}
    </div>
  );
};
